import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  HourglassEmpty as HourglassIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedIcon,
  CheckCircle as CheckIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useAuth } from "contexts/AuthContext";

// ─── Constants ─────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<number, string> = {
  0: "Thành viên",
  1: "Premium",
  2: "Quản trị",
};

const ROLE_REDIRECT: Record<number, string> = {
  2: "/manage",
  1: "/premium",
  0: "/",
};

// ─── Shared sx helpers ──────────────────────────────────────────────────────

const fullScreenCenter = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #1a1a2e 0%, #29263D 50%, #1a1a2e 100%)",
};

// ─── Sub-components ─────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <Box sx={fullScreenCenter}>
      <CircularProgress sx={{ color: "#fbbf24" }} />
    </Box>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <Box sx={fullScreenCenter}>
      <Typography sx={{ color: "#fff" }}>{message}</Typography>
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function PendingPage() {
  const navigate = useNavigate();
  const { profile, signOut, user, loading } = useAuth();

  const role = profile?.role ?? -1;
  const isApproved = role >= 0;

  // Redirect approved users to their appropriate page
  useEffect(() => {
    if (loading || !isApproved) return;
    const destination = ROLE_REDIRECT[role] ?? "/";
    navigate(destination, { replace: true });
  }, [loading, isApproved, role, navigate]);

  if (loading) return <LoadingScreen />;
  if (!user)
    return <ErrorScreen message="Không tìm thấy thông tin tài khoản." />;

  const displayName = profile?.displayName ?? user.displayName ?? "Người dùng";
  const email = profile?.email ?? user.email ?? "";
  const photoURL = profile?.photoURL ?? user.photoURL ?? "";
  const roleLabel = ROLE_LABELS[role] ?? "Chưa xác định";

  const accentColor = isApproved ? "#4caf50" : "#fbbf24";
  const accentAlpha = isApproved ? "rgba(76, 175, 80" : "rgba(251, 191, 36";

  return (
    <Box sx={fullScreenCenter}>
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          background: "linear-gradient(145deg, #2E2B43 0%, #252338 100%)",
          borderRadius: 4,
          width: { xs: "calc(100% - 32px)", sm: 440 },
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          border: `1px solid ${accentAlpha}, 0.15)`,
        }}
      >
        {/* Status Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${accentAlpha}, 0.2), ${accentAlpha}, 0.08))`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 1 },
              "50%": { transform: "scale(1.08)", opacity: 0.8 },
            },
            animation: isApproved ? "none" : "pulse 2s ease-in-out infinite",
          }}
        >
          {isApproved ? (
            <CheckIcon sx={{ fontSize: 44, color: accentColor }} />
          ) : (
            <HourglassIcon sx={{ fontSize: 44, color: accentColor }} />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: 700,
            textAlign: "center",
            fontSize: { xs: "1.2rem", sm: "1.45rem" },
            letterSpacing: "-0.01em",
          }}
        >
          {isApproved ? "Tài khoản đã được kích hoạt" : "Chờ phê duyệt"}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: "#b8b5cc",
            textAlign: "center",
            lineHeight: 1.8,
            maxWidth: 340,
          }}
        >
          {isApproved ? (
            <>
              Chào mừng bạn đến với{" "}
              <Typography
                component="span"
                sx={{ color: accentColor, fontWeight: 600 }}
              >
                P2P – Binance
              </Typography>
              ! Tài khoản{" "}
              <Typography
                component="span"
                sx={{ color: accentColor, fontWeight: 600 }}
              >
                {roleLabel}
              </Typography>{" "}
              của bạn đã sẵn sàng. Đang chuyển hướng…
            </>
          ) : (
            <>
              Tài khoản của bạn đang chờ nhóm phê duyệt. Vui lòng
              chụp màn hình và thông báo cho nhóm để được duyệt sớm.
            
            </>
          )}
        </Typography>

        {/* User Info Card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2.5,
            borderRadius: 3,
            background: "rgba(255, 255, 255, 0.04)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            width: "100%",
          }}
        >
          <Avatar
            src={photoURL}
            alt={displayName}
            sx={{
              width: 48,
              height: 48,
              border: `2px solid ${accentAlpha}, 0.35)`,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
            >
              {displayName}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}
            >
              <EmailIcon sx={{ fontSize: 15, color: "#666" }} />
              <Typography
                variant="body2"
                noWrap
                sx={{ color: "#9a97b0", fontSize: "0.82rem" }}
              >
                {email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Status Badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            px: 2.5,
            py: 1,
            borderRadius: 2,
            background: `${accentAlpha}, 0.1)`,
            border: `1px solid ${accentAlpha}, 0.2)`,
          }}
        >
          {isApproved ? (
            <CheckIcon sx={{ fontSize: 17, color: accentColor }} />
          ) : (
            <VerifiedIcon sx={{ fontSize: 17, color: accentColor }} />
          )}
          <Typography
            variant="body2"
            sx={{ color: accentColor, fontWeight: 600, fontSize: "0.83rem" }}
          >
            {isApproved ? roleLabel : "Đang chờ phê duyệt"}
          </Typography>
        </Box>

        {/* Sign Out Button */}
        <Button
          variant="outlined"
          onClick={signOut}
          startIcon={<LogoutIcon sx={{ fontSize: "18px !important" }} />}
          sx={{
            mt: 0.5,
            color: "#777",
            borderColor: "#3a3a50",
            px: 4,
            py: 1,
            fontSize: "0.85rem",
            textTransform: "none",
            "&:hover": {
              borderColor: accentColor,
              color: accentColor,
              background: `${accentAlpha}, 0.05)`,
            },
          }}
        >
          Đăng xuất
        </Button>
      </Paper>
    </Box>
  );
}
